import { useEffect, useState } from 'react'

/**
 * One tooltip for the whole product, driven by `data-tip`.
 *
 * `title` is a mouse affordance and nothing else: no browser shows one on
 * keyboard focus, a touch screen has nowhere to hover, and a screen reader
 * ignores it outright on an element that already has an accessible name.
 * Sixty of them sat on buttons here — the shortcut a control answers to, what
 * a mode does, why an action would be refused — none of which reached anybody
 * on a keyboard or a phone.
 *
 * A layer rather than a wrapper component, because the alternative was sixty
 * call sites each gaining an element around the control: every one a chance to
 * break a flex row, and every one a place for the aria wiring to be forgotten.
 * A control opts in by carrying `data-tip="…"`, and this does the rest —
 * hover, keyboard focus, long press, Escape to dismiss (WCAG 1.4.13), and
 * `aria-describedby` so the sentence is READ rather than merely displayed.
 */
export function TooltipLayer() {
  const [at, setAt] = useState<{ says: string; left: number; top: number; below: boolean } | null>(
    null,
  )

  useEffect(() => {
    const ID = 'app-tooltip'
    /** The control the bubble currently describes, so the wiring can be undone. */
    let described: Element | null = null

    const place = (node: Element) => {
      const says = node.getAttribute('data-tip')
      if (!says) return
      const box = node.getBoundingClientRect()
      if (box.width === 0 && box.height === 0) return
      /*
       * Guessed, then corrected by the bubble's own measurement below. The
       * first paint has to go somewhere, and a tooltip that jumps once is
       * better than one that flashes at the corner of the screen.
       */
      const below = box.bottom + 40 < window.innerHeight
      setAt({
        says,
        left: box.left + box.width / 2,
        top: below ? box.bottom + 8 : box.top - 8,
        below,
      })
      node.setAttribute('aria-describedby', ID)
      described = node
    }

    const clear = () => {
      described?.removeAttribute('aria-describedby')
      described = null
      setAt(null)
    }

    const tipped = (target: EventTarget | null): Element | null =>
      target instanceof Element ? target.closest('[data-tip]') : null

    const onOver = (e: MouseEvent) => {
      const node = tipped(e.target)
      if (node) place(node)
      else if (described) clear()
    }
    const onOut = (e: MouseEvent) => {
      if (tipped(e.target) === described) clear()
    }
    const onFocus = (e: FocusEvent) => {
      const node = tipped(e.target)
      if (node) place(node)
      else if (described) clear()
    }
    /*
     * A long press is the touch equivalent of a hover: the context menu it
     * raises carries no click count, where a mouse's right-click does. That
     * is the whole of the touch story, and it costs the menu on that control.
     */
    const onContext = (e: MouseEvent) => {
      const node = tipped(e.target)
      if (node && e.detail === 0) {
        e.preventDefault()
        place(node)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      // Dismissed WITHOUT the key travelling on: a tooltip is not a dialog,
      // and closing the drawer behind it is the opposite of what was asked.
      if (e.key === 'Escape' && described) {
        e.stopPropagation()
        clear()
      }
    }

    document.addEventListener('mouseover', onOver)
    document.addEventListener('mouseout', onOut)
    document.addEventListener('focusin', onFocus)
    document.addEventListener('focusout', clear)
    document.addEventListener('contextmenu', onContext)
    // Capture, so it is heard before the dialogs that also answer Escape.
    window.addEventListener('keydown', onKey, { capture: true })
    // A control that scrolls away must not leave its note hanging in the air.
    window.addEventListener('scroll', clear, true)
    return () => {
      document.removeEventListener('mouseover', onOver)
      document.removeEventListener('mouseout', onOut)
      document.removeEventListener('focusin', onFocus)
      document.removeEventListener('focusout', clear)
      document.removeEventListener('contextmenu', onContext)
      window.removeEventListener('keydown', onKey, { capture: true })
      window.removeEventListener('scroll', clear, true)
      described?.removeAttribute('aria-describedby')
    }
  }, [])

  if (!at) return null
  return (
    <div
      id="app-tooltip"
      className={`tip${at.below ? '' : ' tip--above'}`}
      role="tooltip"
      style={{ left: `${at.left}px`, top: `${at.top}px` }}
    >
      {at.says}
    </div>
  )
}
